import { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { dsTemplateService } from '@services/dsTemplateService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import { toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import type { FilterFieldConfig } from '@/types/filters';
import type { DocumentTemplate } from '@workpay/ds-shared/types/template';
import { CreateTemplateModal } from './CreateTemplateModal/CreateTemplateModal';
import PlusIcon from '@assets/icons/PlusIcon';
import { useState } from 'react';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import styles from './TemplateList.module.scss';

type EnrichedTemplate = DocumentTemplate & {
  countryName?: string;
  employmentTypeName?: string;
  documentTypeName?: string;
};

interface TemplateListProps {
  routeTo: string;
  searchParams: TableSearchParams;
}

export function TemplateList({ routeTo, searchParams }: TemplateListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const addNotification = useNotificationStore((s) => s.addNotification);

  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = searchParams;
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = sortBy
    ? { columnId: sortBy, direction: (sortDir ?? 'desc') as 'asc' | 'desc' }
    : undefined;

  const filterFields = useMemo<FilterFieldConfig[]>(() => [
    {
      fieldKey: 'search',
      label: t('K_DS_COLUMN_TEMPLATE_NAME', 'Template Name'),
      type: 'text',
      placeholder: t('K_DS_PLACEHOLDER_SEARCH_TEMPLATE', 'Search template name...'),
    },
    {
      fieldKey: 'status',
      label: t('K_COLUMN_STATUS', 'Status'),
      type: 'select',
      options: [
        { value: 'ACTIVE', label: t('K_OPTION_ACTIVE', 'Active') },
        { value: 'DRAFT', label: t('K_OPTION_DRAFT', 'Draft') },
        { value: 'IN_REVIEW', label: t('K_DS_OPTION_IN_REVIEW', 'In Review') },
        { value: 'INACTIVE', label: t('K_OPTION_INACTIVE', 'Inactive') },
      ],
    },
  ], [t]);

  const {
    filters,
    filterPayload,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
  } = useTableFilters({
    routeTo,
    filtersParam,
    fields: filterFields,
  });

  const sortString = useMemo(() => {
    if (!sort) return 'createdOn,desc';
    const key = sort.columnId === 'name' ? 'name' : sort.columnId === 'createdOn' ? 'createdOn' : sort.columnId;
    return `${key},${sort.direction}`;
  }, [sort]);

  const statusFilter = filterPayload?.filters?.status as string | undefined;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['dsTemplates', { page, size, sort: sortString, statusFilter, filterPayload }],
    queryFn: () => dsTemplateService.getAll({
      page,
      size,
      sort: sortString,
      search: filterPayload?.search as string | undefined,
      status: statusFilter,
    }),
  });
  const listForbidden = isForbiddenError(error);

  const { countries } = useCountries();

  const { data: employmentTypes } = useQuery({
    queryKey: ['employmentTypes', 'all'],
    queryFn: () => getEmploymentTypes({ size: 300 }),
  });

  const { data: documentTypes } = useQuery({
    queryKey: ['documentTypes', 'all'],
    queryFn: () => getDocumentTypes({ size: 300 }),
  });

  const countryMap = useMemo(() => new Map(countries.map((c) => [c.id, c.name])), [countries]);
  const empTypeMap = useMemo(() => new Map((employmentTypes?.content ?? []).map((e) => [e.id, e.name])), [employmentTypes]);
  const docTypeMap = useMemo(() => new Map((documentTypes?.content ?? []).map((d) => [d.id, d.name])), [documentTypes]);

  const templates = useMemo<EnrichedTemplate[]>(() =>
    (data?.content ?? []).map((tpl) => ({
      ...tpl,
      countryName: tpl.countryId ? countryMap.get(tpl.countryId) : undefined,
      employmentTypeName: tpl.employmentTypeId ? empTypeMap.get(tpl.employmentTypeId) : undefined,
      documentTypeName: tpl.documentTypeId ? docTypeMap.get(tpl.documentTypeId) : undefined,
    })),
    [data, countryMap, empTypeMap, docTypeMap],
  );
  const pagination = useMemo(() => toPaginationState(data), [data]);

  const [deleteTarget, setDeleteTarget] = useState<EnrichedTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const audit = useAuditTrigger();
  // Stable opener — row-action deps shouldn't bust on modal open/close.
  const auditOpen = audit.open;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dsTemplateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsTemplates'] });
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_TEMPLATE_DELETED', 'Template deleted successfully') });
      setDeleteTarget(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  // Promote a REVIEW_COMPLETED template to ACTIVE without leaving the list.
  // Backend `approve()` accepts DRAFT / IN_REVIEW / REVIEW_COMPLETED.
  const activateMutation = useMutation({
    mutationFn: (id: string) => dsTemplateService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsTemplates'] });
      addNotification({
        variant: 'success',
        title: t('K_DS_NOTIF_TEMPLATE_ACTIVATED', 'Template activated'),
      });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const columns = useMemo<DataTableColumn<EnrichedTemplate>[]>(() => [
    {
      id: 'name',
      header: t('K_DS_COLUMN_TEMPLATE_NAME', 'Template Name'),
      sortable: true,
      sortKey: 'name',
      cell: (item) => <span className={styles.cellPrimary} title={item.name}>{item.name}</span>,
    },
    {
      id: 'countryName',
      header: t('K_COLUMN_COUNTRY', 'Country'),
      cell: (item) => item.countryName || '\u2014',
    },
    {
      id: 'employmentTypeName',
      header: t('K_COLUMN_EMPLOYMENT_TYPE', 'Employment Type'),
      cell: (item) => item.employmentTypeName || '\u2014',
    },
    {
      id: 'documentTypeName',
      header: t('K_COLUMN_DOCUMENT_TYPE', 'Document Type'),
      cell: (item) => item.documentTypeName || '\u2014',
    },
    {
      id: 'templateFormat',
      header: t('K_DS_COLUMN_FORMAT', 'Format'),
      cell: (item) => (
        <span className={`${styles.badge} ${item.templateFormat === 'DOCX' ? styles.badgeInfo : styles.badgeNeutral}`}>
          {item.templateFormat}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('K_COLUMN_STATUS', 'Status'),
      cell: (item) => (
        <span className={`${styles.badge} ${
          item.status === 'ACTIVE' || item.status === 'REVIEW_COMPLETED' ? styles.badgeSuccess :
          item.status === 'DRAFT' ? styles.badgeWarning :
          item.status === 'IN_REVIEW' ? styles.badgeInfo :
          styles.badgeNeutral
        }`}>
          {item.status === 'IN_REVIEW'
            ? t('K_DS_STATUS_IN_REVIEW', 'In Review')
            : item.status === 'REVIEW_COMPLETED'
              ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')
              : toTitleCase(item.status)}
        </span>
      ),
    },
    {
      id: 'pageCount',
      header: t('K_DS_COLUMN_PAGES', 'Pages'),
      cell: (item) => item.pageCount ?? '—',
    },
    {
      id: 'fieldCount',
      header: t('K_DS_COLUMN_FIELDS', 'Fields'),
      cell: (item) => item.fieldCount ?? '—',
    },
    {
      id: 'createdOn',
      header: t('K_COLUMN_CREATED', 'Created'),
      sortable: true,
      sortKey: 'createdOn',
      cell: (item) => new Date(item.createdOn).toLocaleDateString(),
    },
  ], [t]);

  const rowActions = useCallback((item: EnrichedTemplate) => {
    const actions: ProfileAction[] = [];

    actions.push({
      id: 'view',
      label: t('K_VIEW', 'View'),
      onClick: () => navigate({ to: `${routeTo}/$templateId`, params: { templateId: item.id } }),
    });

    if (hasPermission('CONTRACT_TEMPLATES_UPDATE') && item.status === 'DRAFT') {
      actions.push({
        id: 'edit',
        label: t('K_EDIT', 'Edit'),
        onClick: () => navigate({ to: `${routeTo}/$templateId`, params: { templateId: item.id }, search: { edit: true } }),
      });
    }

    if (hasPermission('CONTRACT_TEMPLATES_APPROVE') && item.status === 'REVIEW_COMPLETED') {
      actions.push({
        id: 'activate',
        label: t('K_DS_ACTIVATE', 'Activate'),
        variant: 'primary',
        onClick: () => activateMutation.mutate(item.id),
      });
    }

    if (hasPermission('CONTRACT_TEMPLATES_DELETE')) {
      actions.push({
        id: 'delete',
        label: t('K_DELETE', 'Delete'),
        onClick: () => setDeleteTarget(item),
      });
    }

    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(item.id, item.name || undefined),
      });
    }

    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, navigate, routeTo, hasPermission, activateMutation, auditOpen]);

  const handleSortChange = useCallback((newSort: SortState | undefined) => {
    void navigate({
      to: routeTo,
      search: (prev: TableSearchParams) => ({
        ...prev,
        page: 0,
        sortBy: newSort?.columnId,
        sortDir: newSort?.direction,
      }),
      replace: true,
    });
  }, [navigate, routeTo]);

  const handlePageChange = useCallback((newPage: number) => {
    void navigate({
      to: routeTo,
      search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
      replace: true,
    });
  }, [navigate, routeTo]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    void navigate({
      to: routeTo,
      search: (prev: TableSearchParams) => ({ ...prev, page: 0, size: newSize }),
      replace: true,
    });
  }, [navigate, routeTo]);

  const handleRowClick = useCallback((item: EnrichedTemplate) => {
    navigate({ to: `${routeTo}/$templateId`, params: { templateId: item.id } });
  }, [navigate, routeTo]);

  const getRowKey = useCallback((item: EnrichedTemplate) => item.id, []);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <p>{t('K_DS_EMPTY_NO_TEMPLATES', 'No templates yet')}</p>
          <p>{t('K_DS_EMPTY_CREATE_FIRST_TEMPLATE', 'Get started by creating your first document template.')}</p>
          {hasPermission('CONTRACT_TEMPLATES_CREATE') && (
            <Button onClick={() => setShowCreateModal(true)} variant="outline">
              <PlusIcon />
              {t('K_DS_BUTTON_NEW_TEMPLATE', 'New Template')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_DS_PAGE_TITLE_TEMPLATES', 'Document Templates')}</h1>
          <p className={styles.subtitle}>{t('K_DS_PAGE_SUBTITLE_TEMPLATES', 'Manage document signing templates')}</p>
        </div>
        {hasPermission('CONTRACT_TEMPLATES_CREATE') && (
          <Button onClick={() => setShowCreateModal(true)}>
            <PlusIcon />
            {t('K_DS_BUTTON_NEW_TEMPLATE', 'New Template')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={templates}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        emptyState={emptyState}
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <AlertModal
        variant="danger"
        title={t('K_DS_MODAL_DELETE_TEMPLATE', 'Delete Template')}
        description={t('K_DS_CONFIRM_DELETE_TEMPLATE', 'Are you sure you want to delete "{name}"?').replace('{name}', deleteTarget?.name ?? '')}
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        primaryLabel={t('K_DELETE', 'Delete')}
        onPrimaryClick={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget.id); }}
        loading={deleteMutation.isPending}
      />

      <CreateTemplateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        routeTo={routeTo}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.DOCUMENT_TEMPLATE, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />
    </div>
  );
}
